import { useEffect, useState } from "react";
import { toast } from "../components/ui/sonner";
import { ArrowsClockwise, Clock, Database } from "@phosphor-icons/react";
import { HealthBadge } from "../components/shared/HealthBadge";
import { EmptyState } from "../components/shared/EmptyState";
import {
  getDataStatus,
  ingestTeams,
  ingestRosters,
  ingestDailyUpdate,
  ingestHistorical,
  ingestPlayerBios,
  getSchedulerStatus,
} from "../lib/api";

const LEAGUE_NAMES = { MLB: "MLB", NPB: "NPB", LMB: "LMB", CPB: "CPBL" };
const HAS_PROVIDER = { MLB: true, NPB: false, LMB: false, CPB: false };
const CURRENT_SEASON = new Date().getFullYear();

const DataStatus = () => {
  const [statuses, setStatuses] = useState([]);
  const [scheduler, setScheduler] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyLeague, setBusyLeague] = useState(null);

  const load = () => {
    setLoading(true);
    getDataStatus()
      .then((data) => setStatuses(data || []))
      .catch(() => setStatuses([]))
      .finally(() => setLoading(false));
    getSchedulerStatus()
      .then(setScheduler)
      .catch(() => setScheduler(null));
  };

  useEffect(() => {
    load();
  }, []);

  const runJob = async (league, label, fn) => {
    setBusyLeague(league);
    try {
      await fn({ league, season: CURRENT_SEASON });
      toast.success(`${label} complete for ${league}`);
      load();
    } catch (e) {
      toast.error(`${label} failed for ${league}`);
    } finally {
      setBusyLeague(null);
    }
  };

  return (
    <div data-testid="data-status-page" className="mx-auto max-w-[1100px] px-4 py-8 sm:px-6">
      <div className="mb-6 flex items-center gap-2">
        <Database size={22} className="text-eb-accent" />
        <h1 className="font-heading text-3xl uppercase tracking-tight text-white">Data Status</h1>
      </div>

      {scheduler && (
        <div data-testid="scheduler-status-banner" className="mb-6 flex items-start gap-3 rounded-lg border border-white/10 bg-eb-surface p-4">
          <Clock size={18} className="mt-0.5 shrink-0 text-eb-accent" />
          <div>
            <p className="font-mono text-xs uppercase tracking-wide text-zinc-300">
              Scheduled sync: {scheduler.enabled ? `checks hourly, runs at most every ${scheduler.min_gap_hours}h` : "disabled"}
              {scheduler.last_run_at ? ` \u00b7 last run ${new Date(scheduler.last_run_at).toLocaleString()}` : ""}
            </p>
            <p className="mt-1 text-xs text-zinc-500">{scheduler.note}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="rounded-lg border border-white/10 bg-eb-surface p-10 text-center font-mono text-xs text-zinc-500">
          Loading data status&hellip;
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {statuses.map((s) => (
            <div key={s.league} data-testid={`data-status-card-${s.league.toLowerCase()}`} className="rounded-lg border border-white/10 bg-eb-surface p-5">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading text-xl uppercase tracking-tight text-white">{LEAGUE_NAMES[s.league] || s.league}</h2>
                <HealthBadge status={s.status} testId={`data-status-badge-${s.league.toLowerCase()}`} />
              </div>
              <div className="grid grid-cols-3 gap-4 font-mono text-sm">
                <div>
                  <p className="text-zinc-500">Games</p>
                  <p className="text-lg text-white" data-testid={`data-status-games-${s.league.toLowerCase()}`}>{s.games}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Teams</p>
                  <p className="text-lg text-white" data-testid={`data-status-teams-${s.league.toLowerCase()}`}>{s.teams}</p>
                </div>
                <div>
                  <p className="text-zinc-500">Players</p>
                  <p className="text-lg text-white" data-testid={`data-status-players-${s.league.toLowerCase()}`}>{s.players}</p>
                </div>
              </div>
              <p className="mt-3 font-mono text-xs text-zinc-500">
                Last successful update:{" "}
                {s.last_successful_update ? new Date(s.last_successful_update).toLocaleString() : "Not imported"}
              </p>

              {HAS_PROVIDER[s.league] ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    data-testid={`data-status-sync-teams-${s.league.toLowerCase()}`}
                    disabled={busyLeague === s.league}
                    onClick={() => runJob(s.league, "Team sync", ingestTeams)}
                    className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-mono text-xs uppercase text-zinc-300 transition-colors hover:border-white/30 disabled:opacity-50"
                  >
                    <ArrowsClockwise size={13} /> Sync Teams
                  </button>
                  <button
                    data-testid={`data-status-sync-rosters-${s.league.toLowerCase()}`}
                    disabled={busyLeague === s.league}
                    onClick={() => runJob(s.league, "Roster sync", ingestRosters)}
                    className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-mono text-xs uppercase text-zinc-300 transition-colors hover:border-white/30 disabled:opacity-50"
                  >
                    <ArrowsClockwise size={13} /> Sync Rosters
                  </button>
                  <button
                    data-testid={`data-status-enrich-bios-${s.league.toLowerCase()}`}
                    disabled={busyLeague === s.league}
                    onClick={() => runJob(s.league, "Player bio enrichment", ingestPlayerBios)}
                    className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-mono text-xs uppercase text-zinc-300 transition-colors hover:border-white/30 disabled:opacity-50"
                  >
                    <ArrowsClockwise size={13} /> Enrich Player Bios
                  </button>
                  <button
                    data-testid={`data-status-daily-update-${s.league.toLowerCase()}`}
                    disabled={busyLeague === s.league}
                    onClick={() => runJob(s.league, "Daily update", ingestDailyUpdate)}
                    className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 font-mono text-xs uppercase text-zinc-300 transition-colors hover:border-white/30 disabled:opacity-50"
                  >
                    <ArrowsClockwise size={13} /> Daily Update
                  </button>
                  <button
                    data-testid={`data-status-historical-import-${s.league.toLowerCase()}`}
                    disabled={busyLeague === s.league}
                    onClick={() => runJob(s.league, "Historical import", ingestHistorical)}
                    className="flex items-center gap-1.5 rounded-md border border-eb-accent/40 bg-eb-accent/10 px-3 py-1.5 font-mono text-xs uppercase text-white transition-colors hover:border-eb-accent disabled:opacity-50"
                  >
                    <ArrowsClockwise size={13} /> Historical Import ({CURRENT_SEASON})
                  </button>
                </div>
              ) : (
                <div className="mt-4">
                  <EmptyState
                    title="No Free Data Source Configured"
                    description="No legitimate free/public API was found for this league yet. The schema and ingestion pipeline are ready to plug one in."
                    testId={`data-status-no-provider-${s.league.toLowerCase()}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DataStatus;
